import { useState } from 'react';
import type { KanbanColuna, KanbanCartao, KanbanConfirmacaoLiberacao, KanbanQuadro, NivelPrioridadeKanban } from '@pieltec/contracts';
import type { DestinoMover } from '../../../lib/kanban/movimento';
import { useKanbanAcoes } from './kanban-acoes-context';
import { useKanbanComandos } from './use-kanban-comandos';
import { KanbanCartaoComponent } from './kanban-cartao';
import { Checkbox } from '../../../components/ui/checkbox';
import { Button } from '../../../components/ui/button';
import { rotuloLiberarSelecionadas, mensagemFalhaLiberar, mensagemLiberarComAvisos, selecaoAposFalhas } from '../../../lib/kanban/avisos';
import { ChevronsLeft, ChevronsRight, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { larguraColuna } from '../../../lib/kanban/volume';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface KanbanFilaEntradaProps {
  coluna: KanbanColuna;
  recolhida?: boolean;
  onAlternarRecolher?: () => void;
  podeArrastar?: boolean;
  loading?: boolean;
  quadro?: KanbanQuadro;
  onMover?: (cartao: KanbanCartao, destino: DestinoMover) => void;
  onPrioridade?: (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => void;
  onResolverConflito?: (cartao: KanbanCartao) => void;
  onDesbloquear?: (cartao: KanbanCartao) => void;
  onSelecionarCartao?: (cartao: KanbanCartao) => void;
}

export function KanbanFilaEntrada({
  coluna,
  recolhida = false,
  onAlternarRecolher,
  podeArrastar = false,
  loading = false,
  quadro,
  onMover,
  onPrioridade,
  onResolverConflito,
  onDesbloquear,
  onSelecionarCartao,
}: KanbanFilaEntradaProps) {
  const { liberar, reordenarFilaEntrada } = useKanbanComandos();
  const { atualizar, notificar } = useKanbanAcoes();
  const [movendo, setMovendo] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [loadingLiberar, setLoadingLiberar] = useState(false);
  const [confirmacao, setConfirmacao] = useState<{
    opIds: string[];
    confirmacoes: KanbanConfirmacaoLiberacao[];
    mensagem: string;
  } | null>(null);

  const raiaUnica = coluna.raias[0];
  const cartoes = raiaUnica ? raiaUnica.cartoes : [];

  const toggleSelecao = (opId: string) => {
    const novo = new Set(selecionados);
    if (novo.has(opId)) {
      novo.delete(opId);
    } else {
      novo.add(opId);
    }
    setSelecionados(novo);
  };

  const solicitarLiberacao = async (confirmacoes?: KanbanConfirmacaoLiberacao[]) => {
    const opIds = confirmacao ? confirmacao.opIds : Array.from(selecionados);
    if (opIds.length === 0) return;
    setLoadingLiberar(true);

    const resposta = await liberar(opIds, confirmacoes);
    
    if (resposta.tipo === 'ok') {
      const resultado = resposta.resultado;
      const liberadas = new Set(resultado?.liberadas.map((item) => item.opId) ?? []);
      const falhas = resultado?.falhas ?? [];
      const pendentes = resultado?.avisosPorOp ?? [];
      const pendentesIds = new Set(pendentes.map((item) => item.opId));
      if (liberadas.size > 0) void atualizar();
      if (falhas.length > 0) notificar('erro', mensagemFalhaLiberar(falhas.length));
      setSelecionados(new Set(
        Array.from(selecionados).filter((opId) => !liberadas.has(opId) && (pendentesIds.has(opId) || falhas.some((falha) => falha.opId === opId))),
      ));

      if (resultado?.requerConfirmacao) {
        const todosAvisos = resultado.avisosPorOp.flatMap(a => a.avisos);
        setConfirmacao({
          opIds: pendentes.map((item) => item.opId),
          confirmacoes: pendentes.map((item) => ({ opId: item.opId, fingerprint: item.fingerprint })),
          mensagem: mensagemLiberarComAvisos(todosAvisos),
        });
        setLoadingLiberar(false);
        return;
      }

      setConfirmacao(null);
      if (falhas.length > 0) {
        const novasSelecoes = selecaoAposFalhas(Array.from(selecionados), falhas);
        setSelecionados(new Set(novasSelecoes));
      } else {
        setSelecionados(new Set());
      }
    } else {
      setConfirmacao(null);
      notificar('erro', resposta.message);
    }

    setLoadingLiberar(false);
  };

  const moverItem = async (opId: string, atual: number, delta: number) => {
    if (movendo || atual + delta < 0 || atual + delta >= cartoes.length) return;
    // Uma reordenação por vez: cliques rápidos calculariam posições sobre a mesma lista antiga.
    setMovendo(true);
    try {
      const resposta = await reordenarFilaEntrada(opId, atual + delta + 1);
      if (resposta.tipo !== 'ok') notificar('erro', resposta.message);
      void atualizar();
    } finally {
      setMovendo(false);
    }
  };

  if (recolhida) {
    return (
      <div className={`flex flex-col bg-slate-50 border-r border-slate-200 shrink-0 h-full ${larguraColuna({ recolhida })}`}>
        <div className="p-2 border-b border-slate-200 flex flex-col items-center bg-white sticky top-0 z-10 shadow-sm gap-2">
          {onAlternarRecolher && (
            <button 
              type="button"
              onClick={onAlternarRecolher}
              aria-expanded={false}
              aria-label={`Expandir coluna ${coluna.titulo}`}
              className="p-1 rounded hover:bg-slate-100 min-h-[32px] min-w-[32px] flex items-center justify-center text-slate-500 hover:text-slate-700"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          )}
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-medium mb-2">
            {coluna.totalCartoes}
          </span>
          <div className="[writing-mode:vertical-rl] rotate-180 text-[12px] font-semibold text-slate-700 uppercase tracking-wider flex-1 overflow-hidden mt-2">
            {coluna.titulo}
          </div>
        </div>
      </div>
    );
  }

  const isCarregandoVazio = loading && cartoes.length === 0;

  return (
    <div className={`flex flex-col bg-slate-50 border-r border-slate-200 shrink-0 h-full ${larguraColuna({ recolhida })}`}>
      <div className="p-3 border-b border-slate-200 flex justify-between items-center bg-white sticky top-0 z-10 shadow-sm">
        <h4 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider truncate flex-1">
          {coluna.titulo}
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-medium">
            {coluna.totalCartoes}
          </span>
          {onAlternarRecolher && (
            <button 
              type="button"
              onClick={onAlternarRecolher}
              aria-expanded={true}
              aria-label={`Recolher coluna ${coluna.titulo}`}
              className="p-1 rounded hover:bg-slate-100 min-h-[32px] min-w-[32px] flex items-center justify-center text-slate-500 hover:text-slate-700"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-2 border-b border-slate-200 bg-white shrink-0">
        <Button 
          variant="default" 
          className="w-full" 
          disabled={selecionados.size === 0 || loadingLiberar || isCarregandoVazio}
          onClick={() => void solicitarLiberacao()}
        >
          {loadingLiberar ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {rotuloLiberarSelecionadas(selecionados.size)}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-3 min-w-0">
        {isCarregandoVazio ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="ui-skeleton h-24 w-full rounded-md" />
            ))}
          </div>
        ) : cartoes.length === 0 ? (
          <div className="text-center p-4">
            <h5 className="font-medium text-slate-700 mb-1">Fila de entrada vazia</h5>
            <p className="text-sm text-slate-500">Nenhuma OP aguardando liberação no momento.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <SortableContext items={cartoes.map(c => c.chave)} strategy={verticalListSortingStrategy}>
            {cartoes.map((cartao, index) => (
              <div key={cartao.chave} className="flex gap-2 items-center">
                {podeArrastar && (
                  <div className="pt-2 shrink-0 self-start flex flex-col items-center gap-1">
                    {loadingLiberar && selecionados.has(cartao.opId) ? (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    ) : (
                      <Checkbox 
                        checked={selecionados.has(cartao.opId)} 
                        onCheckedChange={() => toggleSelecao(cartao.opId)}
                        aria-label={`Selecionar cartão ${cartao.loteCodigo} / ${cartao.opNumero}`}
                      />
                    )}
                    <div className="flex flex-col mt-2 gap-1">
                      <button 
                        onClick={() => void moverItem(cartao.opId, index, -1)} 
                        disabled={movendo || index === 0}
                        className="p-1 rounded bg-slate-200 text-slate-500 hover:bg-slate-300 disabled:opacity-30 min-h-[32px] min-w-[32px] in-data-[density=comfortable]:min-h-[44px] in-data-[density=comfortable]:min-w-[44px] flex items-center justify-center"
                        aria-label="Mover para cima"
                      >
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button 
                        onClick={() => void moverItem(cartao.opId, index, 1)} 
                        disabled={movendo || index === cartoes.length - 1}
                        className="p-1 rounded bg-slate-200 text-slate-500 hover:bg-slate-300 disabled:opacity-30 min-h-[32px] min-w-[32px] in-data-[density=comfortable]:min-h-[44px] in-data-[density=comfortable]:min-w-[44px] flex items-center justify-center"
                        aria-label="Mover para baixo"
                      >
                        <ArrowDown className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <KanbanCartaoComponent 
                    cartao={cartao} 
                    podeArrastar={podeArrastar}
                    quadro={quadro}
                    onMover={onMover}
                    onPrioridade={onPrioridade}
                    onResolverConflito={onResolverConflito}
                    onDesbloquear={onDesbloquear}
                    onSelecionar={onSelecionarCartao}
                  />
                </div>
              </div>
            ))}
            </SortableContext>
          </div>
        )}
      </div>

      <AlertDialog open={confirmacao !== null} onOpenChange={(open) => !open && setConfirmacao(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avisos na liberação</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmacao?.mensagem}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loadingLiberar}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void solicitarLiberacao(confirmacao?.confirmacoes);
              }}
              disabled={loadingLiberar}
            >
              {loadingLiberar && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Liberar mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
